import Add from '@mui/icons-material/Add';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { PrimaryButton } from 'components/forms/atoms/FormButtons';
import type { FC } from 'react';
import { NAVY, RADIUS_ITEM } from 'styles/tokens';

import type { Lane } from '../types';
import CompareLane from './CompareLane';

interface CompareBoardProps {
  groundTruth?: Lane;
  lanes: Lane[];
  anyRunning: boolean;
  onAddLane: () => void;
  onRunAll: () => void;
  onChangeLaneModel: (id: string, model: string) => void;
  onRemoveLane: (id: string) => void;
  onRerunLane: (id: string) => void;
}

/**
 * Multi-model comparison. The ground-truth column (the real stored response) renders first and is
 * never re-run; the model columns each fire an independent /chatCompletion against the same input.
 */
const CompareBoard: FC<CompareBoardProps> = ({
  groundTruth,
  lanes,
  anyRunning,
  onAddLane,
  onRunAll,
  onChangeLaneModel,
  onRemoveLane,
  onRerunLane,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
      <PrimaryButton
        size="small"
        startIcon={<PlayArrow sx={{ fontSize: 18 }} />}
        onClick={onRunAll}
        disabled={anyRunning || lanes.length === 0}
        sx={{ py: 0.7, px: 2.5 }}
      >
        Ejecutar comparación
      </PrimaryButton>
      <Button
        size="small"
        startIcon={<Add sx={{ fontSize: 16 }} />}
        onClick={onAddLane}
        sx={{
          textTransform: 'none',
          fontFamily: 'Jost, sans-serif',
          fontSize: 12.5,
          color: NAVY,
          borderRadius: RADIUS_ITEM,
          '&:hover': { bgcolor: alpha(NAVY, 0.05) },
        }}
      >
        Agregar modelo
      </Button>
      <Typography sx={{ fontSize: 11.5, color: alpha(NAVY, 0.5), fontFamily: 'Jost, sans-serif' }}>
        Replay completo: cada modelo regenera toda la conversación (tool results reusados del original).
      </Typography>
    </Box>

    <Box sx={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'hidden', pb: 1 }}>
      <Box sx={{ display: 'flex', gap: 1.5, height: '100%', alignItems: 'flex-start' }}>
        {groundTruth && (
          <CompareLane lane={groundTruth} onChangeModel={() => {}} onRemove={() => {}} onRerun={() => {}} />
        )}
        {lanes.map((lane) => (
          <CompareLane
            key={lane.id}
            lane={lane}
            onChangeModel={(model) => onChangeLaneModel(lane.id, model)}
            onRemove={() => onRemoveLane(lane.id)}
            onRerun={() => onRerunLane(lane.id)}
          />
        ))}
        {!groundTruth && lanes.length === 0 && (
          <Typography sx={{ fontSize: 12.5, color: alpha(NAVY, 0.45), fontFamily: 'Jost, sans-serif', py: 2 }}>
            Agrega modelos para comparar sus respuestas frente al mismo input.
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

export default CompareBoard;
